import { useEffect, useState, useContext } from "react";
import { DashboardContext } from "../../providers/DashboardProvider";
import { Box, SkeletonText } from "@chakra-ui/react";
import { priceConverter } from "../../utils/priceConverter";

const dataMapping = {
    "Revenue Cycle Temperature Gauge": {
        key: "aiRisk",
        info: "Represents the overall financial health of the revenue cycle. High temperatures indicate potential compliance risks, while low temperatures suggest possible revenue leaks."
    },
    "RI Score": {
        key: "cqScore",
        info: "The RevIntegrity (RI) Score measures the reliability of coding quality based on audit sheet data, including up-coded, down-coded, RVU, and modifier details. It tracks accuracy trends over time, reflecting the overall precision of coding and billing processes, helping identify areas for improvement."
    },
    "RVU Opportunity": {
        key: "rvuOpp",
        info: "Relative Value Units (RVUs) compare billed data and audited data against state-level bell curve distributions. RVUs help measure productivity and efficiency by benchmarking your practice's performance against industry standards."
    },
    "Revenue Breakdown": {
        key: "chartAccuracyBreakdown",
        info: "Displays the total dollar value of charts that have been upcoded or downcoded. Understanding these adjustments helps assess the financial impact of coding practices and identify opportunities for revenue optimization."
    },
    "Outstanding Revenue Opportunity": {
        key: "revenueOpp",
        info: "Represents the dollar value of potential revenue that is missing from billed data. This is calculated by comparing audited and billed data, highlighting areas where revenue improvements can lead to significant financial gains. A percentage value is also calculated to quantify the opportunity."
    },
    "Total Charts": {
        key: "totalUpload",
        info: "Indicates the total number of charts uploaded within the selected date range."
    },
    "Potential Revenue Breakdown": {
        key: "potentialRevenue",
        info: "Estimates the potential revenue impact by comparing the practice’s (billed) coding distribution to state standards. It projects additional revenue based on a predefined number of charts, helping identify revenue recovery opportunities."
    }
};


const AsyncMessageTile = ({ option, triggerNextStep }) => {
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const dashboardContext = useContext(DashboardContext);

    useEffect(() => {
        const fetchMessage = async () => {
            setLoading(true);
            let responseMessage = "";

            if (!dashboardContext?.filterParams) {
                const msg = "Date filter is missing. Please ensure all data is loaded.";
                setMessage(msg);
                setLoading(false);
                triggerNextStep({ value: msg });
                return;
            }

            const dateFilter = dashboardContext.filterParams;
            const selectedDataKey = dataMapping[option]?.key;
            const info = dataMapping[option]?.info;
            const dataValues = dashboardContext[selectedDataKey];

            if (!dataValues || (Array.isArray(dataValues) && !dataValues.length) ||
                (typeof dataValues === "object" && Object.keys(dataValues).length === 0) ||
                (typeof dataValues === "object" && dataValues?.message === "No data present for this account")) {

                const msg = "Not enough information to generate results for the selected option.";
                setMessage(msg);
                setLoading(false);
                triggerNextStep({ value: msg });
                return;
            }

            let selectedData = {
                data: dataValues,
                info: info
            };

            if (option === "Revenue Breakdown" && dashboardContext.chartAccuracyBreakdown && typeof dashboardContext.chartAccuracyBreakdown === 'object') {
                let totalUpcoded = 0;
                let totalDowncoded = 0;
                let totalUpcodedReviewed = 0;
                let totalDowncodedReviewed = 0;

                Object.values(dashboardContext.chartAccuracyBreakdown).forEach((item) => {
                    if (item.rvu_opportunity > 0) {
                        totalDowncoded += item.rvu_opportunity;
                        if (item.review) {
                            totalDowncodedReviewed += item.rvu_opportunity;
                        }
                    } else if (item.rvu_opportunity < 0) {
                        totalUpcoded += item.rvu_opportunity;
                        if (item.review) {
                            totalUpcodedReviewed += item.rvu_opportunity;
                        }
                    }
                });

                totalUpcoded = totalUpcoded ? priceConverter(totalUpcoded) : '$0.00';
                totalDowncoded = totalDowncoded ? priceConverter(totalDowncoded) : '$0.00';

                selectedData = {
                    data: {
                        totalUpcoded,
                        totalDowncoded
                    },
                    info: info
                };
            }
            
            if (!selectedData.info || !selectedData.data || selectedData.data.length === 0) {
                const msg = "No data available for the selected improvement area.";
                setMessage(msg);
                setLoading(false);
                triggerNextStep({ value: msg });
                return;
            }

            try {
                const responseJson = await dashboardContext.generateAiResponse(option, selectedData, dateFilter);
                responseMessage = responseJson ? JSON.stringify(responseJson) : "No insights available.";
            } catch (error) {
                responseMessage = "Error fetching response. Please try again.";
                console.error("AI Response Error:", error);
            }

            setMessage(responseMessage);
            setLoading(false);
            setTimeout(() => triggerNextStep({ value: responseMessage }), 500);
        };

        fetchMessage();
    }, [option]);

    const formatResponse = (response) => {
        let formattedContent = null;
        let responseData;

        try {
            responseData = JSON.parse(response);
        } catch {
            responseData = response;
        }

        try {
            const text = typeof responseData === "object" && responseData.response ? responseData.response : responseData;

            const observationMatch = text.match(/-?\s*Observation:\s*(.*?)(?=-\s*Action:|$)/s);
            const actionMatch = text.match(/-?\s*Action:\s*(.*)/s);

            const observation = observationMatch ? observationMatch[1].trim() : null;
            const action = actionMatch ? actionMatch[1].trim() : null;

            // If neither observation nor action is found, display the original text
            const formattedObservation = observation || "";
            const formattedAction = action || "";

            const formatText = (text) => {
                return text.split("\n").map((line, index) => {
                    line = line.trim();
                    if (line.startsWith("- ") || line.startsWith("• ")) {
                        return <li key={index}>{line.replace(/^-?\s*•?\s*/, "").trim()}</li>;
                    }
                    return <p key={index}>{line}</p>;
                });
            };

            if (!observation && !action) {
                formattedContent = (
                    <div>
                        <p>{text}</p>
                    </div>
                );
            } else {
                formattedContent = (
                    <div>
                        <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textDecoration: 'underline' }}>
                            {option}
                        </div>
                        {formattedObservation && (
                            <>
                                <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '5px' }}>Observation:</div>
                                <ul>{formatText(formattedObservation)}</ul>
                            </>
                        )}
                        {formattedAction && (
                            <>
                                <div style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '10px', marginBottom: '5px' }}>Action:</div>
                                <ul>{formatText(formattedAction)}</ul>
                            </>
                        )}
                    </div>
                );
            }

        } catch (error) {
            console.error("Error formatting response:", error);
            formattedContent = <p>Unable to process the response.</p>;
        }

        return formattedContent;
    };


    return (
        <>
            {loading ? (
                <Box textAlign="center" w="100%">
                    <SkeletonText noOfLines={3} spacing="4" w="80%" mx="auto" />
                </Box>
            ) : (
                <div>{message ? formatResponse(message) : null}</div>
            )}
        </>
    );
};

export default AsyncMessageTile;